import { useEffect, useState } from "react";
import confetti from "canvas-confetti";
import { Mail } from "lucide-react";
import { PetalsCanvas } from "./PetalsCanvas";
import { AUDIO_TRACKS } from "../data/bookData";

// Unlocks at local midnight on her birthday — September 8th (her 20th).
const UNLOCK_AT = new Date(2026, 8, 8, 0, 0, 0);
const BYPASS_KEY = "keepsake-reveal-bypass";
const REVEALED_KEY = "keepsake-revealed";

let giftAudio = null;
const playGiftSong = () => {
  if (!giftAudio) {
    giftAudio = new Audio(AUDIO_TRACKS[0].src);
    giftAudio.loop = true;
    giftAudio.volume = 0.5;
  }
  giftAudio.play().catch(() => {});
};

const pad = (n) => String(n).padStart(2, "0");

export const CountdownGate = ({ children }) => {
  const [now, setNow] = useState(() => Date.now());
  const [bypass, setBypass] = useState(() =>
    localStorage.getItem(BYPASS_KEY) === "1" ||
    new URLSearchParams(window.location.search).get("sneak") === "1"
  );
  const [revealed, setRevealed] = useState(() => localStorage.getItem(REVEALED_KEY) === "1");

  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!bypass && !revealed && now < UNLOCK_AT.getTime()) {
      document.title = "happy birthday tina";
    }
  }, [bypass, revealed, now]);

  const timeUnlocked = now >= UNLOCK_AT.getTime();
  if (bypass || revealed) return children;

  const celebrate = () => {
    confetti({
      particleCount: 180,
      spread: 90,
      origin: { y: 0.6 },
      colors: ["#E8B4B8", "#C87D87", "#D6CBEB", "#D4A359", "#FAE3E8"],
    });
  };

  // One tap = user gesture, so Night Changes starts the second she opens it.
  const breakSeal = () => {
    localStorage.setItem(REVEALED_KEY, "1");
    playGiftSong();
    celebrate();
    setTimeout(() => setRevealed(true), 700);
  };

  if (timeUnlocked) {
    return (
      <div data-testid="unlock-screen" className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#FAF6EE] px-6 text-center">
        <PetalsCanvas />
        <div className="pointer-events-none absolute -left-32 top-16 h-80 w-80 rounded-full bg-[#E6E1F4]/60 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 bottom-24 h-96 w-96 rounded-full bg-[#FAE3E8]/80 blur-3xl" />
        <div className="relative z-10 rise-in">
          <p className="font-hand text-3xl text-[#C87D87] sm:text-4xl">it's midnight, tina.</p>
          <h1 className="mt-4 font-display text-4xl font-semibold tracking-tight text-[#4A2E35] sm:text-6xl">
            Happy <em className="text-[#C87D87]">Twentieth</em> Birthday
          </h1>
          <p className="mx-auto mt-5 max-w-md text-sm leading-relaxed text-[#4A2E35]/65">
            Your gift is right behind this seal. Turn your sound on — it's better with the music.
          </p>
          <button
            data-testid="break-seal-btn"
            onClick={breakSeal}
            className="wax-seal mx-auto mt-10 flex h-24 w-24 items-center justify-center rounded-full"
            aria-label="Break the seal to open your gift"
          >
            <span className="font-display text-3xl font-bold text-[#FFFDF9]">T</span>
          </button>
          <p className="mt-5 text-xs font-medium text-[#4A2E35]/45">tap the seal to open</p>
        </div>
      </div>
    );
  }

  const diff = Math.max(0, UNLOCK_AT.getTime() - now);
  const days = Math.floor(diff / 86400000);
  const hours = Math.floor((diff % 86400000) / 3600000);
  const mins = Math.floor((diff % 3600000) / 60000);
  const secs = Math.floor((diff % 60000) / 1000);

  return (
    <div data-testid="countdown-gate" className="relative flex min-h-screen flex-col items-center justify-center overflow-hidden bg-[#FAF6EE] px-6 text-center">
      <PetalsCanvas />
      <div className="pointer-events-none absolute -left-32 top-16 h-80 w-80 rounded-full bg-[#E6E1F4]/60 blur-3xl" />
      <div className="pointer-events-none absolute -right-24 bottom-24 h-96 w-96 rounded-full bg-[#FAF6EE]/80 blur-3xl" />

      <div className="relative z-10 rise-in">
        <div className="wax-seal mx-auto flex h-24 w-24 items-center justify-center rounded-full">
          <Mail size={34} className="text-[#FFFDF9]" />
        </div>
        <p className="mt-8 font-hand text-2xl text-[#C87D87] sm:text-3xl">something is coming, tina…</p>
        <h1 className="mt-4 font-display text-3xl font-semibold tracking-tight text-[#4A2E35] sm:text-5xl">
          Sealed until <em className="text-[#C87D87]">September 8th</em>
        </h1>
        <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-[#4A2E35]/65">
          This gift opens at midnight, the second your twentieth birthday begins.
          Come back when the candles are ready.
        </p>

        <div data-testid="countdown-timer" className="mt-10 flex items-start justify-center gap-4 sm:gap-6">
          {[["days", days], ["hours", hours], ["min", mins], ["sec", secs]].map(([label, val]) => (
            <div key={label} className="paper-card flex w-16 flex-col items-center rounded-2xl px-2 py-4 sm:w-20">
              <span className="font-display text-3xl font-semibold text-[#4A2E35] sm:text-4xl">{pad(val)}</span>
              <span className="mt-1 text-[10px] font-semibold uppercase tracking-[0.15em] text-[#C87D87]">{label}</span>
            </div>
          ))}
        </div>

        <p className="mt-12 font-hand text-lg text-[#4A2E35]/35">no peeking before midnight</p>
      </div>
    </div>
  );
};
